import { useState } from "react";
import { Anchor, ArrowLeftRight, Building2, Check, ChevronRight, Clock, Coins, Crown, Flame, Gem, Hammer, Hourglass, Landmark, Lock, Megaphone, Paintbrush, Plane, Share2, Ship, ShoppingBag, Swords, Ticket, TrainFront, Trophy, Users, Warehouse, Waves, X, Zap } from "lucide-react";
import { Face } from "@/city3d/Hud";
import { DuelsPanel } from "@/hud/Duels";
import { pointOf } from "./CoinFly";
import { card } from "./Notices";
import { Bar, Btn, Pill, Reel, Ring, Section, Uxd } from "./kit";
import { Advertise, Blimp, Fireworks, Landmarks, Looks, Lottery, Luxury, Uptown } from "./Spend";
import { ACC, SHIP, clock, dur, fmt, useNow } from "./util";

// A stat on its category's pale tile; the number is a slot reel.
const Stat = ({ acc, label, pre = "", v, text }) => (
  <div className="acc-card rounded-[10px] px-2 py-2.5 text-center" style={{ "--acc": ACC[acc] }}>
    <div className="flex items-center justify-center text-xl font-semibold tabular-nums">{text ?? <>{pre}<Reel value={v} /></>}</div>
    <div className="text-xs font-medium">{label}</div>
  </div>
);

function Picker({ econ, i, busy, onStart }) {
  const boost = econ.visitors.boost;
  return (
    <ul className="space-y-0.5 border-t border-x-line px-1.5 py-1.5" data-testid="ship-picker">
      {econ.ships.map((s) => {
        const locked = s.dredge > econ.dredge, Boat = SHIP[s.id] ?? Ship;
        return (
          <li key={s.id}>
            <button type="button" disabled={locked || busy} onClick={() => onStart(i, s)} data-testid={`ship-${s.id}`} title={`${s.name} · pays ${fmt(s.pay)}${boost ? ` (+${boost}%)` : ""}`}
              className="flex w-full items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-left transition-colors hover:bg-x-raised disabled:opacity-50 disabled:hover:bg-transparent">
              <Boat className="h-5 w-5 shrink-0 text-x-accent" strokeWidth={2} />
              <b className="min-w-0 flex-1 truncate text-sm font-semibold">{s.name}</b>
              <span className="flex items-center gap-1 text-xs tabular-nums text-x-mute"><Clock className="h-3 w-3" />{dur(s.min * 60000)}</span>
              <Uxd n={s.pay} className="w-16 text-right text-sm" />
              {locked ? <Pill acc="grey"><Lock className="h-3 w-3" />{s.dredge}</Pill> : <ChevronRight className="h-4 w-4 text-x-mute" />}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function Berth({ b, i, now, busy, uxd, picking, children, onPick, onCollect, onRush }) {
  if (!b.ship) {
    return (
      <li className="rounded-[20px] border border-dashed border-x-control" data-testid="berth">
        <div className="flex items-center gap-3 p-3">
          <Ring value={0} size={56} stroke={5} acc="blue"><Anchor className="h-6 w-6 text-x-mute" /></Ring>
          <b className="flex-1 text-base font-semibold text-x-mute">Berth {i + 1} is free</b>
          <Btn tone={picking ? "soft" : "primary"} className="flex items-center gap-1.5 py-2" onClick={onPick} data-testid="start-ship">
            {picking ? <><X className="h-4 w-4" />Close</> : <><Ship className="h-4 w-4" />Start</>}
          </Btn>
        </div>
        {picking && children}
      </li>
    );
  }
  const done = Date.parse(b.done), at = Date.parse(b.at), ready = now >= done, late = now >= Date.parse(b.late_at);
  const acc = late ? "rose" : ready ? "green" : "blue", Boat = SHIP[b.ship] ?? Ship;
  const tip = late ? "Late: it pays half now" : ready ? `Collect before ${clock(b.late_at)} or it pays half` : `Pays half after ${clock(b.late_at)}`;
  const rush = Math.max(10, Math.floor((b.rush_full * (done - now)) / (done - at)));
  return (
    <li className="acc-card rounded-[20px] p-3" style={{ "--acc": ACC[acc] }} data-testid="berth" title={tip}>
      <div className="flex items-center gap-3">
        <Ring value={ready ? 1 : (now - at) / (done - at)} size={56} stroke={5} acc={acc}><Boat className="h-6 w-6" strokeWidth={2} /></Ring>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-sm font-semibold">{b.name}</div>
          <div className="text-sm font-medium tabular-nums">{late ? "Late, pays half" : `${fmt(b.pay)} UXD`}</div>
        </div>
        {ready
          ? <Btn className="py-2" disabled={busy} onClick={(e) => onCollect(pointOf(e.currentTarget))}>Collect</Btn>
          : <span className="flex flex-col items-end gap-1">
            <span className="text-lg font-semibold leading-none tabular-nums">{dur(done - now)}</span>
            {b.rush_full > 0 && (
              <Btn tone="gold" className="flex items-center gap-1 !px-2 !py-1 text-xs tabular-nums" disabled={busy || uxd < rush} onClick={() => onRush(i, rush)}
                title="Pay to bring it in now" data-testid="rush-ship"><Zap className="h-3 w-3 fill-current" />{fmt(rush)}</Btn>
            )}
          </span>}
      </div>
    </li>
  );
}

function Dock({ econ, eco, now, onCollect }) {
  const [pick, setPick] = useState(null);
  const ready = econ.berths.filter((b) => b.ship && now >= Date.parse(b.done));
  const back = econ.freight?.ready ? econ.freight.pay : 0, n = ready.length + (back ? 1 : 0);
  const start = async (i, s) => {
    if (!(await eco.act("/dock/start", { berth: i, ship: s.id }))) return;
    setPick(null);
    card({ icon: SHIP[s.id], tone: "blue", title: `${s.name} is out`, amount: dur(s.min * 60000) }, { duration: 3500 });
  };
  const rush = async (i, cost) => {
    if (await eco.act("/dock/rush", { berth: i })) card({ icon: Zap, tone: "gold", title: "Rushed in", amount: `-${fmt(cost)}` }, { duration: 3000 });
  };
  return (
    <Section id="dock" icon={Ship} title="Your dock" accent="blue" right={econ.visitors.boost ? <Pill acc="green">+{econ.visitors.boost}%</Pill> : null}
      info="Start a ship in a free berth, then collect when it's in. Collect before the late time or it pays half. Every ship can bring lucky cargo: 2x, 5x or a 20x jackpot.">
      <Btn tone={n ? "primary" : "soft"} className="mb-2 flex w-full items-center justify-center gap-2 py-2.5" disabled={!n || eco.busy}
        onClick={(e) => onCollect(pointOf(e.currentTarget))} data-testid="collect-all">
        <Ship className="h-4 w-4" />{n ? `Collect ${n} · +${fmt(ready.reduce((s, b) => s + b.pay, back))}` : "Collect"}
      </Btn>
      <div className="mb-3 flex items-center justify-center gap-1.5 text-xs text-x-mute" title="Lucky cargo: 2x, 5x or a 20x jackpot">
        Lucky cargo {["2×", "5×", "20×"].map((m) => <Pill key={m} acc="gold">{m}</Pill>)}
      </div>
      <ul className="space-y-2">
        {econ.berths.map((b, i) => (
          <Berth key={i} b={b} i={i} now={now} busy={eco.busy} uxd={econ.uxd} picking={pick === i} onPick={() => setPick(pick === i ? null : i)} onCollect={onCollect} onRush={rush}>
            <Picker econ={econ} i={i} busy={eco.busy} onStart={start} />
          </Berth>
        ))}
      </ul>
    </Section>
  );
}

function Traders({ econ, onFly, onShare }) {
  const { today, boost, step, max } = econ.visitors;
  return (
    <Section id="visitors" icon={ArrowLeftRight} title="Traders" accent="green" right={<Pill acc="green"><Users className="h-3 w-3" />{today.length}</Pill>}
      info={`Every trader today adds +${step}% to what your ships pay, up to +${max}%.`}>
      <div className="flex items-center gap-4">
        <Ring value={boost / max} size={72} stroke={7} acc="green"><span className="text-lg font-semibold tabular-nums">+{boost}%</span></Ring>
        {today.length ? (
          <div className="flex flex-1 flex-wrap gap-2" data-testid="visitor-faces">
            {today.map((p) => <button key={p.k} type="button" onClick={() => onFly(p)} className="transition-transform hover:scale-110"><Face p={p} size={34} ring={ACC.green} /></button>)}
          </div>
        ) : <div className="flex-1 text-sm text-x-mute" data-testid="visitors-empty">No traders yet today. Share your dock to bring some in.</div>}
      </div>
      <Btn tone="green" className="mt-3 flex w-full items-center justify-center gap-1.5 py-2" onClick={onShare} data-testid="share-dock"><Share2 className="h-3.5 w-3.5" />Come trade at my dock</Btn>
    </Section>
  );
}

// An upgrade tile: icon, level and the price on its button. The detail sits in the tooltip.
function Up({ icon: Icon, title, level, sub, cost, uxd, busy, onBuy, testId }) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-[20px] border border-x-line p-2.5 text-center" data-testid={testId}
      title={`${sub}${cost != null && uxd < cost ? ` · need ${fmt(cost - uxd)} more` : ""}`}>
      <Icon className="h-6 w-6 text-x-accent" strokeWidth={2} />
      <b className="text-sm font-semibold leading-tight">{title}</b>
      <span className="text-xs tabular-nums text-x-mute">{level}</span>
      {cost == null ? <Pill acc="grey" className="mt-1">Max</Pill>
        : <Btn className="mt-1 flex w-full items-center justify-center gap-1 !px-1 tabular-nums" disabled={busy || uxd < cost} onClick={onBuy}><Coins className="h-3.5 w-3.5" />{fmt(cost)}</Btn>}
    </div>
  );
}

function Upgrades({ econ, eco, onCollect, onChanged }) {
  const { costs, warehouse: w, uxd, dredge, berths, ships, freight: f, rail } = econ;
  const nextShip = ships.filter((s) => s.dredge > dredge).sort((a, b) => a.dredge - b.dredge)[0];
  const buy = (path, text) => async () => {
    if (!(await eco.act(path))) return;
    onChanged?.();
    card({ icon: Check, tone: "green", title: text }, { duration: 3000 });
  };
  const sendFreight = async () => {
    const r = await eco.act("/freight");
    if (r) card({ icon: TrainFront, tone: "gold", title: `Goods on the ${clock(r.freight.leaves)} train`, amount: `+${fmt(r.freight.pay)}` }, { duration: 4000 });
  };
  return (
    <Section id="upgrades" icon={Hammer} title="Upgrades" accent="blue" right={<span className="tabular-nums" title="What you've put into your dock and building">{fmt(econ.invested)} invested</span>}>
      <div className="grid grid-cols-2 gap-2">
        <Up testId="up-berth" icon={Anchor} title="Berth" level={`${berths.length} → ${berths.length + 1}`} sub={`Berth ${berths.length + 1}: one more ship at a time`}
          cost={costs.berth} uxd={uxd} busy={eco.busy} onBuy={buy("/dock/berth", "New berth built")} />
        <Up testId="up-dredge" icon={Waves} title="Dredge" level={`Level ${dredge}`} uxd={uxd} cost={costs.dredge} busy={eco.busy} onBuy={buy("/dock/dredge", "Dredged deeper")}
          sub={nextShip ? `${nextShip.name} needs level ${nextShip.dredge}` : "Deepest water"} />
        <Up testId="up-warehouse" icon={Warehouse} title="Warehouse" level={w.level ? `Level ${w.level}` : "New"} uxd={uxd} cost={costs.warehouse} busy={eco.busy} onBuy={buy("/dock/warehouse", "Warehouse ready")}
          sub={w.level ? `+${fmt(w.rate)} UXD/h passive` : "Earns UXD while you're away"} />
        <Up testId="up-floor" icon={Building2} title="Floor" level={`${econ.floors?.total ?? 1} → ${(econ.floors?.total ?? 1) + 1}`} uxd={uxd} cost={costs.floor}
          busy={eco.busy} onBuy={buy("/dock/floor", "Added a floor to your building")} sub="Build your tower taller" />
      </div>
      {econ.upkeep.per_day > 0 && (
        <div className="mt-2 flex items-center justify-between text-xs text-x-mute" data-testid="upkeep"
          title="Extra berths, deeper water and a warehouse cost a little to keep up. It comes out of what you collect, never more than that.">
          <span>Upkeep</span><span className="tabular-nums">{fmt(econ.upkeep.per_day)} UXD a day</span>
        </div>
      )}
      {w.level > 0 && (
        <div className="mt-3 rounded-[20px] border border-x-line p-3" data-testid="warehouse-till">
          <div className="flex items-center gap-3" title={`Holds up to ${w.hold_h}h of income`}>
            <Warehouse className="h-6 w-6 shrink-0 text-x-mute" strokeWidth={2} />
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-baseline justify-between text-sm"><Uxd n={w.till} /><span className="tabular-nums text-x-mute">of {fmt(w.cap)}</span></div>
              <Bar value={w.till / w.cap} />
            </div>
            <Btn disabled={(w.till < 1 && !f?.ready) || eco.busy} onClick={(e) => onCollect(pointOf(e.currentTarget))}>Collect</Btn>
          </div>
          <div className="mt-2.5 flex items-center gap-2 border-t border-x-line pt-2.5 text-xs" data-testid="freight">
            <TrainFront className="h-4 w-4 shrink-0 text-x-gold" />
            <span className="min-w-0 flex-1 text-x-mute">
              {f ? (f.ready ? `Back from the mainland: ${fmt(f.pay)} UXD to collect` : `On the train: ${fmt(f.pay)} UXD back ${clock(f.back)}`)
                : `Rail the goods to the mainland for ${rail.mult}%, back in ${rail.back_h}h`}
            </span>
            {!f && (
              <Btn tone="gold" className="!py-1 text-xs" disabled={eco.busy || w.till < rail.min} onClick={sendFreight} data-testid="freight-send"
                title={w.till < rail.min ? `The train takes ${fmt(rail.min)} UXD of goods or more` : `Next train ${clock(rail.next)}`}>Send</Btn>
            )}
          </div>
        </div>
      )}
    </Section>
  );
}

function Week({ econ, now }) {
  const w = econ.week, inRow = w.rank && w.rank <= w.seats;
  return (
    <Section id="week" icon={Trophy} title="This week" accent="rose" right={<span className="flex items-center gap-1 tabular-nums"><Hourglass className="h-3.5 w-3.5" />{dur(Date.parse(w.until) - now)}</span>}
      info={inRow ? "You hold a Millionaires' Row seat right now. Keep earning to keep it."
        : `The top ${w.seats} earners move into Millionaires' Row every Monday. #${w.seats} has ${fmt(w.row)} so far.`}>
      <div className="grid grid-cols-3 gap-2">
        <Stat acc="gold" label="Earned" pre="+" v={w.earned} />
        {w.rank ? <Stat acc="grey" label="Rank" pre="#" v={w.rank} /> : <Stat acc="grey" label="Rank" text="–" />}
        {inRow ? <Stat acc="rose" label="On the Row" text={<Gem className="h-5 w-5" />} /> : <Stat acc="rose" label="To the Row" pre="+" v={Math.max(0, w.row - w.earned) + 1} />}
      </div>
      <div className="mt-3"><Bar value={w.row ? w.earned / w.row : w.earned > 0 ? 1 : 0} color={ACC.rose} /></div>
    </Section>
  );
}

export function EconomyTab({ econ, eco, challenge, onCollect, onFly, onLot, onShare, onFireworks }) {
  const now = useNow(eco.clockOffset);
  return (
    <>
      <Dock econ={econ} eco={eco} now={now} onCollect={onCollect} />
      <Uptown econ={econ} eco={eco} now={now} onLot={onLot} />
      <Section id="duels" icon={Swords} title="Duels" accent="rose">
        <DuelsPanel econ={econ} challenge={challenge} onChanged={eco.refresh} onVisit={onFly} />
      </Section>
      <Traders econ={econ} onFly={onFly} onShare={onShare} />
      <Week econ={econ} now={now} />
    </>
  );
}

const least = (xs) => {
  const v = xs.filter((x) => x > 0);
  return v.length ? Math.min(...v) : 0;
};

// Tonight's lottery up front, then one tile per stall, each with what's live there or its lowest price; a tile jumps to its section.
function Aisles({ econ, eco, now }) {
  const own = econ.flex?.owned ?? [], l = econ.lottery, sky = econ.blimp?.active, fw = econ.fireworks?.until;
  const left = (iso) => iso && Date.parse(iso) > now && dur(Date.parse(iso) - now);
  const tiles = [
    ["upgrades", "Upgrades", Hammer, "blue", least(Object.values(econ.costs ?? {}))],
    ["lottery", "Lottery", Ticket, "gold", l?.price, l?.mine > 0 && `${fmt(l.mine)} tickets`],
    ["blimp", "Blimp", Plane, "gold", econ.blimp?.price, left(sky?.until) && `Up · ${left(sky.until)}`],
    ["fireworks", "Fireworks", Flame, "rose", econ.fireworks?.price, left(fw) && `${left(fw)} left`],
    ["luxury", "Luxury", Crown, "gold", least(Object.entries(econ.flex?.prices ?? {}).filter(([id]) => !own.includes(id)).map(([, p]) => p)),
      own.length > 0 && `${own.length}/${Object.keys(econ.flex?.prices ?? {}).length} owned`],
    ["landmarks", "Landmarks", Landmark, "green", least((econ.landmarks ?? []).map((s) => s.price))],
    ["ads", "Ads", Megaphone, "blue", least(econ.ad_slots.map((s) => s.price))],
    ["looks", "Looks", Paintbrush, "rose", least(Object.values(econ.look_prices))],
  ];
  const jump = (id) => document.querySelector(`[data-section="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const draw = new Date(now);
  draw.setUTCHours(24, 0, 0, 0);
  const ticket = async () => {
    if (await eco.act("/lottery", { n: 1 })) card({ icon: Ticket, tone: "gold", title: "1 ticket in tonight's draw", amount: `-${fmt(l.price)}` }, { duration: 3000 });
  };
  return (
    <Section id="shop" icon={ShoppingBag} title="Shop" accent="gold" right={<Uxd n={econ.uxd} className="text-x-ink" />}
      info="Everything you can spend UXD on. Upgrades and luxury pieces count as invested, so your worth on the leaderboard stays whole.">
      {l && (
        <div className="acc-card mb-2 flex items-center gap-3 rounded-[20px] p-3" style={{ "--acc": ACC.gold }} data-testid="shop-featured">
          <span className="acc-fill grid h-11 w-11 shrink-0 place-items-center rounded-full"><Ticket className="h-5 w-5" strokeWidth={2.2} /></span>
          <button type="button" onClick={() => jump("lottery")} className="min-w-0 flex-1 text-left leading-tight">
            <span className="block text-xs font-medium">Tonight's lottery pot</span>
            <span className="money block text-xl font-semibold tabular-nums text-x-ink">{fmt(l.pot)} <span className="text-xs font-medium text-x-mute">UXD</span></span>
            <span className="block text-[11px] tabular-nums text-x-mute">Draw in {dur(draw - now)} · {fmt(l.players)} {l.players === 1 ? "player" : "players"}</span>
          </button>
          <Btn tone="gold" className="shrink-0 py-2 tabular-nums" disabled={eco.busy || econ.uxd < l.price} onClick={ticket} data-testid="shop-ticket">Ticket · {fmt(l.price)}</Btn>
        </div>
      )}
      <div className="grid grid-cols-4 gap-2" data-testid="shop-aisles">
        {tiles.map(([id, label, Icon, acc, price, live]) => (
          <button key={id} type="button" onClick={() => jump(id)} data-testid={`aisle-${id}`} style={{ "--acc": ACC[acc] }}
            className="acc-card flex flex-col items-center gap-1.5 rounded-[14px] px-1 pb-2 pt-2.5 text-center transition-opacity hover:opacity-85">
            <span className="acc-fill grid h-8 w-8 place-items-center rounded-full"><Icon className="h-4 w-4" strokeWidth={2.2} /></span>
            <b className="text-xs font-semibold leading-tight text-x-ink">{label}</b>
            <span className={`max-w-full truncate px-1 text-[11px] tabular-nums ${live ? "font-semibold" : price && econ.uxd >= price ? "text-x-ink" : "text-x-mute"}`}>
              {live || (price ? fmt(price) : "–")}
            </span>
          </button>
        ))}
      </div>
    </Section>
  );
}

export function ShopTab({ econ, eco, onCollect, onFireworks }) {
  const now = useNow(eco.clockOffset);
  return (
    <>
      <Aisles econ={econ} eco={eco} now={now} />
      <Upgrades econ={econ} eco={eco} onCollect={onCollect} onChanged={onFireworks} />
      <Lottery econ={econ} eco={eco} now={now} />
      <Blimp econ={econ} eco={eco} now={now} onBlimp={onFireworks} />
      <Fireworks econ={econ} eco={eco} now={now} onFireworks={onFireworks} />
      <Luxury econ={econ} eco={eco} onBuy={onFireworks} />
      <Landmarks econ={econ} eco={eco} now={now} onSponsored={onFireworks} />
      <Advertise econ={econ} eco={eco} now={now} />
      <Looks econ={econ} eco={eco} />
    </>
  );
}
